import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../../helpers/api_helper';

export interface CreateBankPayload {
  // Common
  account_name: string;
  status?: 'active' | 'inactive';
  // IMPS fields
  name?: string;
  account_number?: string;
  ifsc_code?: string;
  // UPI fields
  upi_handle?: string;
  phone_number?: string;
}

export interface Bank {
  id: number;
  name: string;
  account_name: string;
  account_number: string;
  ifsc_code: string;
  upi_handle: string;
  phone_number: string;
  qr_code: string | null;
  qr_code_url: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

interface PaymentState {
  creatingBank: boolean;
  createBankError: string | null;
  lastCreatedBank: Bank | null;
  message: string | null;
}

const initialState: PaymentState = {
  creatingBank: false,
  createBankError: null,
  lastCreatedBank: null,
  message: null,
};

export const createBankAccount = createAsyncThunk(
  'payments/createBankAccount',
  async (payload: CreateBankPayload, { rejectWithValue }) => {
    try {
      const form = new FormData();
      if (payload.name) form.append('name', payload.name);
      if (payload.account_name) form.append('account_name', payload.account_name);
      if (payload.account_number) form.append('account_number', payload.account_number);
      if (payload.ifsc_code) form.append('ifsc_code', payload.ifsc_code);
      if (payload.upi_handle) form.append('upi_handle', payload.upi_handle);
      if (payload.phone_number) form.append('phone_number', payload.phone_number);
      form.append('status', payload.status ?? 'active');

      const res = await api.post('/admin/banks/', form);
      if (!res?.data?.success) {
        return rejectWithValue(res?.data?.message || 'Failed to create bank');
      }
      return res.data as { success: boolean; message: string; bank: Bank };
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to create bank';
      return rejectWithValue(msg);
    }
  }
);

export const updateBankAccount = createAsyncThunk(
  'payments/updateBankAccount',
  async (
    { id, payload }: { id: number; payload: Partial<CreateBankPayload> },
    { rejectWithValue }
  ) => {
    try {
      const form = new FormData();
      if (payload.name) form.append('name', payload.name);
      if (payload.account_name) form.append('account_name', payload.account_name);
      if (payload.account_number) form.append('account_number', payload.account_number);
      if (payload.ifsc_code) form.append('ifsc_code', payload.ifsc_code);
      if (payload.upi_handle) form.append('upi_handle', payload.upi_handle);
      if (payload.phone_number) form.append('phone_number', payload.phone_number);
      if (payload.status) form.append('status', payload.status);
      const res = await api.put(`/admin/banks/${id}/`, form);
      if (!res?.data?.success) {
        return rejectWithValue(res?.data?.message || 'Failed to update bank');
      }
      return res.data as { success: boolean; message: string; bank: Bank };
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to update bank';
      return rejectWithValue(msg);
    }
  }
);

const paymentSlice = createSlice({
  name: 'payments',
  initialState,
  reducers: {
    clearPaymentState(state) {
      state.creatingBank = false;
      state.createBankError = null;
      state.lastCreatedBank = null;
      state.message = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(createBankAccount.pending, (state) => {
        state.creatingBank = true;
        state.createBankError = null;
        state.lastCreatedBank = null;
        state.message = null;
      })
      .addCase(createBankAccount.fulfilled, (state, action: any) => {
        state.creatingBank = false;
        state.createBankError = null;
        state.lastCreatedBank = action.payload?.bank ?? null;
        state.message = action.payload?.message ?? 'Created';
      })
      .addCase(createBankAccount.rejected, (state, action: any) => {
        state.creatingBank = false;
        state.createBankError = action.payload || 'Failed to create bank';
      })
      .addCase(updateBankAccount.pending, (state) => {
        state.creatingBank = true;
        state.createBankError = null;
        state.message = null;
      })
      .addCase(updateBankAccount.fulfilled, (state, action: any) => {
        state.creatingBank = false;
        state.createBankError = null;
        state.lastCreatedBank = action.payload?.bank ?? null;
        state.message = action.payload?.message ?? 'Updated';
      })
      .addCase(updateBankAccount.rejected, (state, action: any) => {
        state.creatingBank = false;
        state.createBankError = action.payload || 'Failed to update bank';
      });
  }
});

export const { clearPaymentState } = paymentSlice.actions;
export default paymentSlice.reducer;


